import React from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory, Link } from 'react-router-dom';
import {
  Container,
  Col,
  Card,
  Row,
  Table,
  Input,
  Button,
  InputGroup,
  FormFeedback,
} from 'reactstrap';
import { useFormik } from 'formik';
import { useBoolean } from 'usehooks-ts';
import * as Yup from 'yup';

import images from 'assets/images';
import CHANGE_QUANTITY_STATUS from 'constants/changeQuantityStatus';
import { LOCATIONS } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import MODAL_TYPE from 'constants/modalType';
import { PARAM_SETTING } from 'constants/setting';
import { createNewRoomValidation } from 'utils/validate';
// import randomIDString from 'utils/randomId';

import AppBreadcrumb from 'components/App/Breadcrumb';
import AppButton from 'components/App/Button';
import AppInputValidation from 'components/App/InputValidation';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import AppMeta from 'components/App/Meta';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import FormField from 'components/AddLocation/FormField';
import FormFieldWithLine from 'components/AddLocation/FormFieldWithLine';
import SectionWrapper from 'components/AddLocation/SectionWrapper';
import AppSelectValidation from 'components/App/SelectValidation';
import Loading from 'components/App/Loading';

import { Pencil } from 'lucide-react';
import styles from './CreateNewRoom.module.scss';

const { INCREASE, REDUCE } = CHANGE_QUANTITY_STATUS;

function CreateNewRoom(props) {
  const {
    isLoadingCreateNewRoom,
    isLoadingUpdateRoomOfPlace,
    isLoadingGetRoomDetailOfPlace,
    isLoadingGetPlaceOtaData,
    isLoadingGetRoomPriceTypes,
    isLoadingUpdateRoomPriceTypes,

    placeOtaData,
    placeDetail,
    setting,
    roomDetailOfPlace,
    roomPriceTypes,

    actionGetRoomDetailOfPlace,
    actionGetPlaceDetail,
    actionGetPlaceOtaData,
    actionGetRoomPriceTypes,
    actionUpdateRoomPriceTypes,
    actionCreateNewRoom,
    actionDeleteRoomOfPlace,
    actionUpdateRoomOfPlace,
    actionOpenModalCreateSuccess,
  } = props;

  const history = useHistory();
  const location = useLocation();

  const placeId = new URLSearchParams(location.search).get('place');
  const roomId = new URLSearchParams(location.search).get('room');

  const breadcrumbList = React.useMemo(() => ([
    {
      name: 'Chỗ nghỉ',
      isActive: false,
      link: `${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${placeId}`,
    },
    {
      name: 'Loại phòng',
      isActive: false,
      link: `${LOCATIONS.LOCATION_DETAIL.ROOMS_OF_PLACE}?place=${placeId}`,
    },
    {
      name: `${roomId ? 'Chỉnh sửa phòng' : 'Tạo phòng mới'}`,
      isActive: true,
    },
  ]), [placeId, roomId]);

  const {
    value: isOpenModalDeleteRoom,
    setFalse: onCloseModalDeleteRoom,
    setTrue: onOpenModalDeleteRoom,
  } = useBoolean(false);
  const hydratedRoomPriceTypeIdsRef = React.useRef('');
  const hydratedBedTypesKeyRef = React.useRef('');

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      // roomName: roomId ? roomDetailOfPlace.roomName : '',
      name: roomId ? roomDetailOfPlace.name : '',
      quantity: roomId ? roomDetailOfPlace.quantity : 1,
      totalBedrooms: roomId ? roomDetailOfPlace.totalBedrooms : 0,
      totalLivingRooms: roomId ? roomDetailOfPlace.totalLivingRooms : 0,
      totalBathrooms: roomId ? roomDetailOfPlace.totalBathrooms : 0,
      standardGuests: roomId
        ? (roomDetailOfPlace.standardGuests || roomDetailOfPlace.standardCapacity || 2)
        : 2,
      maxCapacity: roomId
        ? (roomDetailOfPlace.maxCapacity
          || roomDetailOfPlace.maxGuests
          || roomDetailOfPlace.maxCustomers
          || (roomDetailOfPlace.maxAdults + roomDetailOfPlace.maxChildren)
          || 4)
        : 4,
      maxAdults: roomId ? roomDetailOfPlace.maxAdults : 3,
      maxChildren: roomId ? roomDetailOfPlace.maxChildren : 2,
      bedTypes: [],
      hotelLinkRoomId: roomId
        ? (roomDetailOfPlace.hotelLinkRoomId || roomDetailOfPlace.otaRoomId || '')
        : '',
      roomPriceTypeHotelinkIds: {},
    },
    validationSchema: Yup.object({
      // roomName: createNewRoomValidation.roomName,
      name: createNewRoomValidation.name,
      quantity: createNewRoomValidation.quantity,
      totalBedrooms: createNewRoomValidation.totalBedrooms,
      totalLivingRooms: createNewRoomValidation.totalLivingRooms,
      totalBathrooms: createNewRoomValidation.totalBathrooms,
      bedTypes: createNewRoomValidation.bedTypes,
      standardGuests: createNewRoomValidation.standardGuests,
      maxCapacity: createNewRoomValidation.maxCapacity,
      maxAdults: createNewRoomValidation.maxAdults,
      maxChildren: createNewRoomValidation.maxChildren,
    }),

    onSubmit: (values) => {
      let actionSubmitForm;

      const onCallbackCreateRoomSuccess = (() => {
        history.push(`${LOCATIONS.LOCATION_DETAIL.ROOMS_OF_PLACE}?place=${placeId}`);
        actionOpenModalCreateSuccess(MODAL_TYPE.CREATE_ROOM_SUCCESS);
      });

      const result = {
        // roomName: values.roomName,
        name: values.name,
        quantity: values.quantity,
        totalBedrooms: values.totalBedrooms,
        totalLivingRooms: values.totalLivingRooms,
        totalBathrooms: values.totalBathrooms,
        standardGuests: Number(values.standardGuests),
        maxCapacity: Number(values.maxCapacity),
        maxAdults: Number(values.maxAdults),
        maxChildren: Number(values.maxChildren),
        bedTypes: values.bedTypes
          .filter((bed) => bed.quantity !== 0)
          .map(({ bedType, quantity, checked }) => ({ bedType, quantity, checked })),
        placeId,
        roomId,
      };

      if (roomId) {
        result.otaRoomId = values.hotelLinkRoomId || '';
        actionSubmitForm = () => {
          actionUpdateRoomOfPlace({ result });
        };
      } else {
        actionSubmitForm = () => {
          actionCreateNewRoom({
            result,
            callback: onCallbackCreateRoomSuccess,
          });
        };
      }

      actionSubmitForm();
      // console.log('error', error);
      // const firstErrorElement = document.querySelector('.invalid-feedback');
      // Thay '.error-message' bằng class hoặc id của phần tử chứa lỗi
      // if (firstErrorElement) {
      //   firstErrorElement.scrollIntoView({ behavior: 'smooth' });
      // }
    },
  });

  const onToggleSelectAllBedTypes = () => {
    const currentBeds = validation.values.bedTypes || [];
    const isAllChecked = currentBeds.length > 0 && currentBeds.every((bedType) => bedType.checked);
    const nextChecked = !isAllChecked;
    const updatedBedTypes = currentBeds.map((item) => {
      let quantity = 0;
      if (nextChecked) {
        quantity = item.quantity > 0 ? item.quantity : 1;
      }

      return {
        ...item,
        checked: nextChecked,
        quantity,
      };
    });
    validation.setFieldValue('bedTypes', updatedBedTypes, false);
  };

  const onSelectBed = (bedTypeId) => (e) => {
    const isChecked = e.target.checked;
    const currentBeds = validation.values.bedTypes || [];
    const updatedBedTypes = currentBeds.map((item) => {
      if (item._id === bedTypeId || item.bedType === bedTypeId) {
        let quantity = 0;
        if (isChecked) {
          quantity = item.quantity > 0 ? item.quantity : 1;
        }

        return {
          ...item,
          checked: isChecked,
          quantity,
        };
      }
      return item;
    });
    validation.setFieldValue('bedTypes', updatedBedTypes, false);
  };

  const onChangeQuantity = (bedTypeId, actionType) => () => {
    const currentBeds = validation.values.bedTypes || [];
    const updatedBedTypes = currentBeds.map((item) => {
      if (item._id === bedTypeId || item.bedType === bedTypeId) {
        let newQuantity = item.quantity || 0;

        if (actionType === INCREASE) {
          newQuantity += 1;
        } else if (actionType === REDUCE) {
          newQuantity = Math.max(0, newQuantity - 1);
        }

        return {
          ...item,
          quantity: newQuantity,
          checked: newQuantity > 0,
        };
      }
      return item;
    });
    validation.setFieldValue('bedTypes', updatedBedTypes, false);
  };

  const onConfirmDeleteRoom = React.useCallback(() => {
    history.push(`${LOCATIONS.LOCATION_DETAIL.CONVENIENCE_SERVICE}?place=${placeId}`);
    actionDeleteRoomOfPlace({ placeId, roomId, callback: onCloseModalDeleteRoom });
  }, [actionDeleteRoomOfPlace, history, onCloseModalDeleteRoom, placeId, roomId]);

  const onUpdateRoomPriceTypes = React.useCallback(() => {
    const data = roomPriceTypes.map((item) => ({
      _id: item._id,
      otaRatePlanId: validation.values.roomPriceTypeHotelinkIds[item._id] || '',
    }));

    actionUpdateRoomPriceTypes({
      roomId,
      placeId,
      data,
      callback: () => actionGetRoomPriceTypes({ placeId, roomId }),
    });
  }, [
    actionGetRoomPriceTypes,
    actionUpdateRoomPriceTypes,
    placeId,
    roomId,
    roomPriceTypes,
    validation.values.roomPriceTypeHotelinkIds,
  ]);

  // const roomNameOptions = React.useMemo(() => {
  //   if (setting[PARAM_SETTING.ROOM_NAMES]) {
  //     const newRoomNameOptions = [];
  //     setting[PARAM_SETTING.ROOM_NAMES].forEach((item) => {
  //       item.children.forEach((childrenItem) => {
  //         newRoomNameOptions.push({
  //           label: childrenItem.vi,
  //           value: childrenItem.name,
  //           group: item.vi,
  //         });
  //       });
  //     });

  //     return newRoomNameOptions;
  //   }

  //   return [];
  // }, [setting]);

  const hasOtaCredentials = Boolean(
    placeDetail?.otaHotelId && placeDetail?.otaHotelChannelKey,
  );

  React.useEffect(() => {
    if (placeId) {
      actionGetPlaceDetail({ placeId });
    }
  }, [actionGetPlaceDetail, placeId]);

  React.useEffect(() => {
    if (placeId && roomId && hasOtaCredentials) {
      actionGetPlaceOtaData({ placeId });
    }
  }, [actionGetPlaceOtaData, hasOtaCredentials, placeId, roomId]);

  React.useEffect(() => {
    if (roomId) {
      actionGetRoomDetailOfPlace({ placeId, roomId });
      actionGetRoomPriceTypes({ placeId, roomId });
    }
  }, [actionGetRoomDetailOfPlace, actionGetRoomPriceTypes, placeId, roomId]);

  const ownedHotelLinkRoomId = React.useMemo(
    () => roomDetailOfPlace?.hotelLinkRoomId || roomDetailOfPlace?.otaRoomId || '',
    [roomDetailOfPlace],
  );

  const hotelLinkRoomOptions = React.useMemo(
    () => (placeOtaData || []).map((item) => ({
      label: `${item.Name} (${item.RoomId})`,
      value: item.RoomId,
      isDisabled: Boolean(
        item.isLinked && item.RoomId !== ownedHotelLinkRoomId,
      ),
    })),
    [ownedHotelLinkRoomId, placeOtaData],
  );

  const hotelLinkRatePlanOptions = React.useMemo(() => {
    const selectedRoom = (placeOtaData || []).find(
      (item) => item.RoomId === validation.values.hotelLinkRoomId,
    );

    return (selectedRoom?.RatePlans || []).map((ratePlan) => ({
      label: `${ratePlan.Name} (${ratePlan.RatePlanId})`,
      value: ratePlan.RatePlanId,
    }));
  }, [placeOtaData, validation.values.hotelLinkRoomId]);

  const onChangeHotelLinkRoomId = React.useCallback((selected) => {
    validation.setFieldValue('hotelLinkRoomId', selected?.value || '', false);

    const clearedHotelinkIds = Object.keys(validation.values.roomPriceTypeHotelinkIds || {})
      .reduce((acc, key) => ({
        ...acc,
        [key]: '',
      }), {});

    validation.setFieldValue('roomPriceTypeHotelinkIds', clearedHotelinkIds, false);
  }, [validation]);

  const onChangeHotelinkPriceId = React.useCallback((priceTypeId) => (selected) => {
    validation.setFieldValue(
      `roomPriceTypeHotelinkIds.${priceTypeId}`,
      selected?.value || '',
      false,
    );
  }, [validation]);

  React.useEffect(() => {
    if (!roomPriceTypes.length) return;

    const nextHydratedKey = JSON.stringify(
      roomPriceTypes.map((item) => ({
        _id: item._id,
        hotelinkPriceId: item.otaRatePlanId || item.hotelinkPriceId || '',
      })),
    );

    if (hydratedRoomPriceTypeIdsRef.current === nextHydratedKey) return;

    const roomPriceTypeHotelinkIds = roomPriceTypes.reduce((acc, item) => ({
      ...acc,
      [item._id]: item.otaRatePlanId || item.hotelinkPriceId || '',
    }), {});

    hydratedRoomPriceTypeIdsRef.current = nextHydratedKey;
    validation.setFieldValue('roomPriceTypeHotelinkIds', roomPriceTypeHotelinkIds, false);
  }, [roomPriceTypes, validation]);

  React.useEffect(() => {
    if (!setting[PARAM_SETTING.BED_TYPES] || !setting[PARAM_SETTING.BED_TYPES][0]?.children) {
      return;
    }

    const bedTypesDataKey = roomId
      ? `${roomId}_${roomDetailOfPlace?._id || ''}_${(roomDetailOfPlace?.bedTypes || []).map((b) => `${b.bedType}-${b.quantity}-${b.checked}`).join('_')}`
      : 'new_room_initial';

    if (hydratedBedTypesKeyRef.current === bedTypesDataKey) {
      return;
    }

    const cloneBedTypesList = setting[PARAM_SETTING.BED_TYPES];
    let newBedTypes = [];

    if (roomId && roomDetailOfPlace) {
      newBedTypes = cloneBedTypesList[0].children.map((item) => {
        const found = roomDetailOfPlace.bedTypes?.find(({ bedType }) => bedType === item.name);
        if (found) {
          return {
            _id: found._id || item._id || item.name,
            label: item.vi,
            bedType: item.name,
            quantity: found.quantity || 0,
            checked: found.checked !== undefined ? Boolean(found.checked) : (found.quantity > 0),
          };
        }

        return {
          _id: item._id || item.name,
          label: item.vi,
          bedType: item.name,
          quantity: 0,
          checked: false,
        };
      });
    } else {
      newBedTypes = cloneBedTypesList[0].children.map((item) => ({
        _id: item._id || item.name,
        label: item.vi,
        bedType: item.name,
        quantity: 0,
        checked: false,
      }));
    }

    hydratedBedTypesKeyRef.current = bedTypesDataKey;
    validation.setFieldValue('bedTypes', newBedTypes, false);
  }, [roomDetailOfPlace, roomId, setting]);

  return (
    <div className={`page-content ${styles.Page}`}>
      <AppMeta
        name={roomId ? 'Chỉnh sửa phòng' : 'Tạo phòng mới'}
      />

      <Loading
        loaderOnPage
        loading={isLoadingCreateNewRoom
          || isLoadingUpdateRoomOfPlace
          || isLoadingGetRoomDetailOfPlace
          || isLoadingGetPlaceOtaData
          || isLoadingGetRoomPriceTypes
          || isLoadingUpdateRoomPriceTypes}
      />

      <Container fluid>
        <AppBreadcrumb
          pageTitle={roomId ? 'Chỉnh sửa phòng' : 'Tạo phòng mới'}
          breadcrumbList={breadcrumbList}
        />

        <div className="Card__Wrapper">
          <Col md="8">
            {roomId && (
            <SectionWrapper className="mb-24 PriceTypeSection">
              <div className="PriceTypeSection__Header">
                <span className="Title">Tùy chọn Loại giá</span>
                <Button
                  type="button"
                  className="PriceTypeSection__EditButton"
                  onClick={() => history.push(`${LOCATIONS.LOCATION_DETAIL.LIST_PRICE_TYPE}?place=${placeId}`)}
                >
                  <Pencil width={16} height={16} color="var(--bookese-blue-100)" />
                  Đổi loại giá
                </Button>
              </div>

              {roomPriceTypes.map((item, index) => (
                <Row
                  key={item._id}
                  className={index > 0 ? 'PriceTypeSection__Row' : ''}
                >
                  <Col md="6">
                    <FormField label="Loại giá">
                      <input
                        type="text"
                        className="form-control PriceTypeSection__ReadonlyInput"
                        value={item.priceType?.name || ''}
                        disabled
                        readOnly
                      />
                    </FormField>
                  </Col>

                  <Col md="6" className="mt-3 mt-md-0">
                    <FormField isHiddenLabel className="PriceTypeSection__HotelinkField">
                      <label className="require" htmlFor={`hotelinkPriceId-${item._id}`}>
                        HotelLink Price Id
                        <i
                          className="mdi mdi-information PriceTypeSection__InfoIcon"
                          title="Nhập ID loại giá từ hệ thống Hotelink để đồng bộ giá và tình trạng phòng."
                        />
                      </label>

                      <div
                        className={
                          (!hasOtaCredentials || !validation.values.hotelLinkRoomId)
                            ? 'HotelLinkRoomSelect--Disabled'
                            : undefined
                        }
                      >
                        <AppSelectValidation
                          isHiddenSearchable
                          name={`roomPriceTypeHotelinkIds.${item._id}`}
                          id={`hotelinkPriceId-${item._id}`}
                          placeholder="Chọn Hotelink Price ID (OTA ID)"
                          options={hotelLinkRatePlanOptions}
                          value={validation.values.roomPriceTypeHotelinkIds?.[item._id] || ''}
                          onChange={onChangeHotelinkPriceId(item._id)}
                          validation={validation}
                          isLoading={isLoadingGetPlaceOtaData}
                          isDisabled={!hasOtaCredentials || !validation.values.hotelLinkRoomId}
                        />
                      </div>

                      <span className="Subtitle">
                        Chọn ID loại giá từ hệ thống HotelLink để đồng bộ giá và tình trạng phòng.
                      </span>
                      {/* {index === 0 && (
                      )} */}
                    </FormField>
                  </Col>
                </Row>
              ))}

              <div className="PriceTypeSection__Footer">
                <AppButton
                  type="button"
                  loading={isLoadingUpdateRoomPriceTypes}
                  classes="Button Button__Save PriceTypeSection__SaveButton"
                  onClick={onUpdateRoomPriceTypes}
                >
                  Lưu
                </AppButton>
              </div>
            </SectionWrapper>
            )}

            <SectionWrapper title="Vui lòng chọn" className="mb-24">
              {/* <FormField label="Tên phòng" isRequired>
                  <AppSelectValidation
                    isHiddenSearchable
                    placeholder="Vui lòng chọn"
                    name="roomName"
                    key="roomName"
                    options={roomNameOptions}
                    value={validation.values.roomName}
                    onChange={onChangeRoomNameSelection}
                    validation={validation}
                  />

                  <span className="Subtitle">
                    Đây là tên mà khách sẽ thấy trên trang web Bookese.com
                  </span>
                </FormField> */}

              {/* <FormField label="Tên tùy chọn (không bắt buộc)">
                  <AppInputValidation
                    isShowCounter
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Nhập tên tuỳ chọn"
                    validation={validation}
                  // value={validation.values.name}
                  />

                  <span className="Subtitle">
                    Tạo tên tùy chọn cho riêng Quý vị tham khảo (không bắt buộc).
                  </span>
                </FormField> */}

              <FormField label="Tên phòng" isRequired>
                <AppInputValidation
                  isShowCounter
                  name="name"
                  id="name"
                  type="text"
                  placeholder="Nhập tên phòng"
                  validation={validation}
                  value={validation.values.name}
                />

                <span className="Subtitle">
                  Đây là tên mà khách sẽ thấy trên trang web Bookese.com
                </span>
              </FormField>

              {roomId && (
                <FormField label="Select Hotel Link Room Id">
                  <div className={!hasOtaCredentials ? 'HotelLinkRoomSelect--Disabled' : undefined}>
                    <AppSelectValidation
                      isHiddenSearchable
                      name="hotelLinkRoomId"
                      id="hotelLinkRoomId"
                      placeholder="Chọn Hotel Link Room Id"
                      options={hotelLinkRoomOptions}
                      value={validation.values.hotelLinkRoomId}
                      onChange={onChangeHotelLinkRoomId}
                      validation={validation}
                      isLoading={isLoadingGetPlaceOtaData}
                      isDisabled={!hasOtaCredentials}
                    />
                  </div>
                </FormField>
              )}

              <Row className="mb-24">
                <Col md="6">
                  <FormField label="Số phòng (loại này)" isRequired>
                    <AppInputNumberValidation
                      name="quantity"
                      keyName="quantity"
                      validation={validation}
                      formatOptions={{
                        // Số thập phân có thể nhập được ( hiện tại là không được nhập )
                        decimalScale: 0,
                      }}
                    />
                  </FormField>
                </Col>

                <Col md="6">
                  <FormField label="Số phòng ngủ">
                    <AppInputNumberValidation
                      name="totalBedrooms"
                      keyName="totalBedrooms"
                      validation={validation}
                      formatOptions={{
                        // Số thập phân có thể nhập được ( hiện tại là không được nhập )
                        decimalScale: 0,
                      }}
                    />
                  </FormField>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormField label="Số phòng khách">
                    <AppInputNumberValidation
                      name="totalLivingRooms"
                      keyName="totalLivingRooms"
                      validation={validation}
                      formatOptions={{
                        // Số thập phân có thể nhập được ( hiện tại là không được nhập )
                        decimalScale: 0,
                      }}
                    />
                  </FormField>
                </Col>

                <Col md="6">
                  <FormField label="Số phòng tắm">
                    <AppInputNumberValidation
                      name="totalBathrooms"
                      keyName="totalBathrooms"
                      validation={validation}
                      formatOptions={{
                        // Số thập phân có thể nhập được ( hiện tại là không được nhập )
                        decimalScale: 0,
                      }}
                    />
                  </FormField>
                </Col>
              </Row>
            </SectionWrapper>
            {/* <Card className="Card ">
            </Card> */}

            <SectionWrapper title="Sức chứa" className="mb-24">
              <FormFieldWithLine
                label="Số khách tiêu chuẩn"
                isRequired
                subtitle="Thiết lập giá dựa trên số khách tiêu chuẩn"
              >
                <div>
                  <div className={styles.NumberStepper}>
                    <input
                      type="number"
                      name="standardGuests"
                      className={styles.StepperInput}
                      value={validation.values.standardGuests}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          validation.setFieldValue('standardGuests', '');
                          return;
                        }
                        const val = Number(raw);
                        validation.setFieldValue('standardGuests', Number.isNaN(val) ? '' : val);
                      }}
                      min={1}
                      max={20}
                    />
                    <div className={styles.CaretsGroup}>
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Tăng số khách tiêu chuẩn"
                        className={styles.CaretBtn}
                        onClick={() => {
                          const val = Number(validation.values.standardGuests) || 0;
                          validation.setFieldValue('standardGuests', Math.min(20, val + 1));
                        }}
                      >
                        <i className="mdi mdi-chevron-up" />
                      </button>
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Giảm số khách tiêu chuẩn"
                        className={styles.CaretBtn}
                        onClick={() => {
                          const val = Number(validation.values.standardGuests) || 0;
                          validation.setFieldValue('standardGuests', Math.max(1, val - 1));
                        }}
                      >
                        <i className="mdi mdi-chevron-down" />
                      </button>
                    </div>
                  </div>
                  {validation.errors?.standardGuests && (
                    <FormFeedback type="invalid" className="d-block font-size-12 mt-1 text-end">
                      {validation.errors.standardGuests}
                    </FormFeedback>
                  )}
                </div>
              </FormFieldWithLine>

              <FormFieldWithLine
                label="Sức chứa tối đa"
                isRequired
                subtitle="Bao gồm trẻ em và người lớn lưu trú trong phòng này"
              >
                <div>
                  <div className={styles.NumberStepper}>
                    <input
                      type="number"
                      name="maxCapacity"
                      className={styles.StepperInput}
                      value={validation.values.maxCapacity}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          validation.setFieldValue('maxCapacity', '');
                          return;
                        }
                        const val = Number(raw);
                        validation.setFieldValue('maxCapacity', Number.isNaN(val) ? '' : val);
                      }}
                      min={1}
                      max={20}
                    />
                    <div className={styles.CaretsGroup}>
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Tăng sức chứa tối đa"
                        className={styles.CaretBtn}
                        onClick={() => {
                          const val = Number(validation.values.maxCapacity) || 0;
                          validation.setFieldValue('maxCapacity', Math.min(20, val + 1));
                        }}
                      >
                        <i className="mdi mdi-chevron-up" />
                      </button>
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Giảm sức chứa tối đa"
                        className={styles.CaretBtn}
                        onClick={() => {
                          const val = Number(validation.values.maxCapacity) || 0;
                          validation.setFieldValue('maxCapacity', Math.max(1, val - 1));
                        }}
                      >
                        <i className="mdi mdi-chevron-down" />
                      </button>
                    </div>
                  </div>
                  {validation.errors?.maxCapacity && (
                    <FormFeedback type="invalid" className="d-block font-size-12 mt-1 text-end">
                      {validation.errors.maxCapacity}
                    </FormFeedback>
                  )}
                </div>
              </FormFieldWithLine>

              <FormFieldWithLine
                label="Số người lớn tối đa"
                isRequired
              >
                <div>
                  <div className={styles.NumberStepper}>
                    <input
                      type="number"
                      name="maxAdults"
                      className={styles.StepperInput}
                      value={validation.values.maxAdults}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          validation.setFieldValue('maxAdults', '');
                          return;
                        }
                        const val = Number(raw);
                        validation.setFieldValue('maxAdults', Number.isNaN(val) ? '' : val);
                      }}
                      min={1}
                      max={20}
                    />
                    <div className={styles.CaretsGroup}>
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Tăng số người lớn tối đa"
                        className={styles.CaretBtn}
                        onClick={() => {
                          const val = Number(validation.values.maxAdults) || 0;
                          if (val < 20) {
                            validation.setFieldValue('maxAdults', val + 1);
                          }
                        }}
                      >
                        <i className="mdi mdi-chevron-up" />
                      </button>
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Giảm số người lớn tối đa"
                        className={styles.CaretBtn}
                        onClick={() => {
                          const val = Number(validation.values.maxAdults) || 0;
                          if (val > 1) {
                            validation.setFieldValue('maxAdults', val - 1);
                          }
                        }}
                      >
                        <i className="mdi mdi-chevron-down" />
                      </button>
                    </div>
                  </div>
                  {validation.errors?.maxAdults && (
                    <FormFeedback type="invalid" className="d-block font-size-12 mt-1 text-end">
                      {validation.errors.maxAdults}
                    </FormFeedback>
                  )}
                </div>
              </FormFieldWithLine>

              <FormFieldWithLine
                label="Số trẻ em tối đa"
                isRequired
              >
                <div>
                  <div className={styles.NumberStepper}>
                    <input
                      type="number"
                      name="maxChildren"
                      className={styles.StepperInput}
                      value={validation.values.maxChildren}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          validation.setFieldValue('maxChildren', '');
                          return;
                        }
                        const val = Number(raw);
                        validation.setFieldValue('maxChildren', Number.isNaN(val) ? '' : val);
                      }}
                      min={0}
                      max={20}
                    />
                    <div className={styles.CaretsGroup}>
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Tăng số trẻ em tối đa"
                        className={styles.CaretBtn}
                        onClick={() => {
                          const val = Number(validation.values.maxChildren) || 0;
                          if (val < 20) {
                            validation.setFieldValue('maxChildren', val + 1);
                          }
                        }}
                      >
                        <i className="mdi mdi-chevron-up" />
                      </button>
                      <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Giảm số trẻ em tối đa"
                        className={styles.CaretBtn}
                        onClick={() => {
                          const val = Number(validation.values.maxChildren) || 0;
                          if (val > 0) {
                            validation.setFieldValue('maxChildren', val - 1);
                          }
                        }}
                      >
                        <i className="mdi mdi-chevron-down" />
                      </button>
                    </div>
                  </div>
                  {validation.errors?.maxChildren && (
                    <FormFeedback type="invalid" className="d-block font-size-12 mt-1 text-end">
                      {validation.errors.maxChildren}
                    </FormFeedback>
                  )}
                </div>
              </FormFieldWithLine>
            </SectionWrapper>

            {/* <Card className="Card">
            </Card> */}

            <SectionWrapper title="Tùy chọn giường" className="Card__Container">
              {validation.errors?.bedTypes && (
              <FormFeedback type="invalid" className="d-block font-size-12">
                {validation.errors.bedTypes}
              </FormFeedback>
              )}

              <Table
                hover
                striped
                className="mb-0"
              >
                <thead>
                  <tr>
                    <th className="col-md-2">
                      <Input
                        id="selectAllCheckbox"
                        className="cursor-pointer"
                        type="checkbox"
                        onChange={onToggleSelectAllBedTypes}
                        checked={
                          Boolean(
                            validation.values.bedTypes?.length
                            && validation.values.bedTypes.every((bedType) => bedType.checked),
                          )
                        }
                      />
                    </th>

                    <th className="Table__Header__Title col-md-6">
                      Loại giường
                      <code>*</code>
                    </th>

                    <th className="Table__Header__Title col-md-4">
                      Số lượng giường
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {(validation.values.bedTypes || []).map((item) => (
                    <tr
                      key={item._id || item.bedType}
                      className="Table__Body__Row"
                    >
                      <th scope="row">
                        <Input
                          className="cursor-pointer"
                          type="checkbox"
                          checked={Boolean(item.checked)}
                          onChange={onSelectBed(item._id || item.bedType)}
                        />
                      </th>

                      <td>
                        {item.label}
                      </td>

                      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                      <td>
                        <InputGroup className="InputGroup">
                          <Button
                            type="button"
                            color="primary"
                            className="InputGroup__Btn InputGroup__Btn--Decrease"
                            onClick={onChangeQuantity(item._id || item.bedType, REDUCE)}
                            disabled={!item.quantity || item.quantity === 0}
                          >
                            <i className="mdi mdi-minus" />
                          </Button>

                          <input
                            type="number"
                            name="quantity"
                            className="form-control InputGroup__Input text-center"
                            value={item.quantity || 0}
                            placeholder="0"
                            readOnly
                          />

                          <Button
                            type="button"
                            color="primary"
                            className="InputGroup__Btn InputGroup__Btn--Increase"
                            onClick={onChangeQuantity(item._id || item.bedType, INCREASE)}
                          >
                            <i className="mdi mdi-plus" />
                          </Button>
                        </InputGroup>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </SectionWrapper>
            {/* <Card className="Card">
            </Card> */}
          </Col>
        </div>

        <Card className="Card__Button">
          <div className="Card__Button__Container">
            {
              roomId && (
                <div className="Button__Delete">
                  <button
                    className="btn-minimal red-100"
                    type="button"
                    onClick={onOpenModalDeleteRoom}
                  >
                    Xóa
                  </button>
                </div>
              )
            }

            <div className="Card__Button__Wrapper">
              <Link
                to={{ pathname: LOCATIONS.LOCATION_DETAIL.ROOMS_OF_PLACE, search: `?place=${placeId}` }}
                disabled={isLoadingCreateNewRoom
                  || isLoadingUpdateRoomOfPlace}
                className="Button Button__Cancel"
              >
                Hủy
              </Link>

              <AppButton
                loading={isLoadingCreateNewRoom
                  || isLoadingUpdateRoomOfPlace}
                classes="Button Button__Save"
                type="submit"
                onClick={validation.handleSubmit}
              >
                Lưu
              </AppButton>
            </div>
          </div>
        </Card>

        <ModalRequestAction
          isOpen={isOpenModalDeleteRoom}
          icon={images.modalGarbageCan.default}
          textRequest="Bạn có chắc muốn xóa phòng này không?"
          onCloseModal={onCloseModalDeleteRoom}
          onConfirm={onConfirmDeleteRoom}
        />
      </Container>
    </div>
  );
}

CreateNewRoom.propTypes = {
  isLoadingCreateNewRoom: PropTypes.bool.isRequired,
  isLoadingUpdateRoomOfPlace: PropTypes.bool.isRequired,
  isLoadingGetRoomDetailOfPlace: PropTypes.bool.isRequired,
  isLoadingGetPlaceOtaData: PropTypes.bool.isRequired,
  isLoadingGetRoomPriceTypes: PropTypes.bool.isRequired,
  isLoadingUpdateRoomPriceTypes: PropTypes.bool.isRequired,

  placeOtaData: PropTypes.arrayOf(PropTypes.shape({
    Name: PropTypes.string,
    RoomId: PropTypes.string,
    isLinked: PropTypes.bool,
    RatePlans: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string,
      RatePlanId: PropTypes.string,
    })),
  })),
  placeDetail: PropTypes.instanceOf(Object),
  setting: PropTypes.instanceOf(Object),
  roomDetailOfPlace: PropTypes.instanceOf(Object),
  roomPriceTypes: PropTypes.arrayOf(PropTypes.instanceOf(Object)),

  actionGetRoomDetailOfPlace: PropTypes.func.isRequired,
  actionGetPlaceDetail: PropTypes.func.isRequired,
  actionGetPlaceOtaData: PropTypes.func.isRequired,
  actionGetRoomPriceTypes: PropTypes.func.isRequired,
  actionUpdateRoomPriceTypes: PropTypes.func.isRequired,
  actionOpenModalCreateSuccess: PropTypes.func.isRequired,
  actionCreateNewRoom: PropTypes.func.isRequired,
  actionDeleteRoomOfPlace: PropTypes.func.isRequired,
  actionUpdateRoomOfPlace: PropTypes.func.isRequired,
};

CreateNewRoom.defaultProps = {
  placeOtaData: [],
  placeDetail: INITIAL_VALUE.OBJECT,
  setting: INITIAL_VALUE.OBJECT,
  roomDetailOfPlace: INITIAL_VALUE.OBJECT,
  roomPriceTypes: [],
};

export default CreateNewRoom;
